<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="productGridModalActive"
        class="overlay critical-hide overlay--has-drag
        lookbook-product-list-grid shelf shelf--up lookbook-product-grid-modal"
      >
        <button
          type="button"
          class="lookbook-product-grid-modal__overlay window-overlay"
          @click="closeProductGridModal"
        />
        <lookbook-product-grid-modal :key="`lookbook-product-grid-modal-${blockIndex}`" />
      </div>
    </transition>
  </teleport>
  <div
    v-bind="$attrs"
    ref="scrollElement"
    class="lookbook-images critical-component-clear"
    :class="{
      'lookbook-product-grid': imageGalleryGrid,
      'lookbook-product-grid--horizontal-scroll': enableHorizontalScroll,
    }"
  >
    <component
      :is="enableHorizontalScroll ? 'horizontal-scroll-manager' : 'div'"
      ref="lookbookImagesContainer"
      class="lookbook-images__container grid"
    >
      <template
        v-for="(block, index) of blocks"
        :key="index"
      >
        <div
          v-if="block.video || block.image"
          class="lookbook-images__image-wrapper col"
          :class="getBlockClasses(block.singleItem)"
        >
          <video-player
            v-if="block.video"
            class="lookbook-images__video"
            :class="{
              'hidden-controls': block.products && block.products.length
            }"
            :image="block.video.preview_image"
            :intersection-observer="{ enable: block.videoAutoPlay }"
            :sources="block.video.sources"
            :is-dark="block.darkControls"
            :controls="{
              hide: true,
              id: `${block.video.id}`,
              muteButton: block.videoAudioControls,
            }"
            :poster="block.videoPoster"
            :is-active="productCardLocation.key === 'plpHotspot' ? true : false"
            :attributes="productCardLocation.key === 'plpHotspot' ? { autoplay: true } : ''"
          />

          <responsive-image
            v-else-if="(!block.video && !isMobile) || (!block.video && !block.mobileImage)"
            class="lookbook-images__image"
            :image="getImage(block.image)"
            :lazyload="lazyload"
            :padding="false"
            ratio="1:1"
            sizes="50vw"
          />

          <responsive-image
            v-else-if="!block.video && isMobile && block.mobileImage"
            class="lookbook-images__image"
            :image="getImage(block.mobileImage)"
            :padding="false"
            ratio="1:1"
            sizes="50vw"
          />

          <button
            v-if="!block.video"
            class="lookbook-images__shopping"
            tabindex="-1"
            type="button"
            aria-hidden="true"
            @click.prevent="showProducts(index, block)"
          />

          <button
            v-if="block.products &&
              block.products.length &&
              visibleProductsBlockIndex !== index
            "
            class="lookbook-images__atc"
            type="button"
            @click="showProducts(index, block)"
          >
            <icon-cart />

            <span
              class="
                lookbook-images__atc-label
                text-body-2xs-regular
              "
              v-text="$string('lookbook.shop_the_look')"
            />
          </button>

          <p
            v-else-if="block.title"
            class="lookbook-images__title text-body-2xs-regular"
            :class="getBlockTitleClasses(block)"
            :style="{
              'color': `${block.titleColor}`
            }"
            v-text="block.title"
          />

          <transition name="fade">
            <div
              v-if="block.products.length && visibleProductsBlockIndex === index"
              class="lookbook-images__product-cards-wrapper"
            >
              <div class="lookbook-images__buttons-wrapper">
                <button
                  v-if="blockConfigs[index]?.productSelected"
                  class="lookbook-images__close-atc"
                  type="button"
                  show-icon
                  @click="closeAtc(index)"
                >
                  <icon-arrow-left />

                  <span
                    class="
                      lookbook-images__close-atc-label
                      text-body-2xs-regular
                    "
                    v-text="
                      showBackInStock
                        ? $string('lookbook.back_to_look')
                        : $string('lookbook.return_to_look')
                    "
                  />
                </button>

                <button
                  v-else
                  class="lookbook-images__products-close"
                  type="button"
                  @click="toggleProducts(-1, [])"
                >
                  <span
                    class="visually-hidden"
                    v-text="$string('accessibility.close')"
                  />
                  <icon-close aria-hidden="true" />
                </button>
              </div>

              <div
                class="lookbook-images__carousel-wrapper"
                :class="{ 'grid-wrapper': blockConfigs[index]?.productSelected }"
              >
                <splide
                  :ref="`splide-${index}`"
                  class="lookbook-images__carousel"
                  :has-track="false"
                  :options="getSplideConfigs(block.products.length, index)"
                >
                  <splide-track>
                    <splide-slide
                      v-for="(product, productIndex) of block.products"
                      :key="productIndex"
                    >
                      <product-card
                        :id="`splide-${index}-${productIndex}`"
                        tabindex="-1"
                        :product="product"
                        card-layout="small"
                        :show-wishlist="false"
                        :enable-flat-image="false"
                        enable-variant-image
                        :image-is-linked="false"
                        :show-badges="false"
                        :show-swatches="false"
                        :load-secondary-image="false"
                        :show-product-content="false"
                        :image-background-color="getImageBackgroundColor(blockConfigs[index])"
                        :show-quick-add="!blockConfigs[index]?.productSelected"
                        enable-image-carousel
                        :carousel-limit="4"
                        custom-atc
                        :product-card-location="productCardLocation"
                        @custom-quick-add="handleAddToCart(index, productIndex)"
                      />
                    </splide-slide>
                  </splide-track>

                  <div class="lookbook-images__arrows splide__arrows">
                    <button
                      class="splide__arrow splide__arrow--prev"
                      type="button"
                    >
                      <span
                        class="visually-hidden"
                        v-text="$string('accessibility.carousel.previous')"
                      />

                      <icon-arrow-left />
                    </button>

                    <button
                      class="splide__arrow splide__arrow--next"
                      type="button"
                    >
                      <span
                        class="visually-hidden"
                        v-text="$string('accessibility.carousel.next')"
                      />

                      <icon-arrow-right />
                    </button>
                  </div>
                </splide>

                <div
                  v-if="blockConfigs[index]?.productSelected"
                  class="lookbook-images__product-details"
                  :class="{
                    'bis-active': showBackInStock
                  }"
                >
                  <quick-add-to-cart
                    v-if="visibleProductsBlockIndex > -1 && blockConfigs[index].viewedProduct"
                    size-guide-position="top"
                    :number-of-sizes-per-row="7"
                    show-confirmation-button
                    show-view-product-button
                    :product="blockConfigs[index].viewedProduct"
                    :show-back-in-stock="showBackInStock"
                    show-product-details
                    is-stl-type
                    :product-card-location="productCardLocation"
                    :bis-settings="{
                      showCloseButton: false,
                      namespace: `lookbook-${blockConfigs[index].viewedProduct.handle}`,
                      marketingText: $string('product.back_in_stock.marketing_opt_in')
                    }"
                    @bis-active="handleBackInStockModal"
                  />
                </div>
              </div>
            </div>
          </transition>
        </div>
      </template>
    </component>
  </div>
  <klaviyo-bis-message v-if="isMobile && showSuccessMessage" />
</template>

<script>

/**
 * Vue: Lookbook images (lookbook-images)
 * -----------------------------------------------------------------------------
 * The lookbook section is used to feature editorial images that feature
 * products that can be purchased to buy the look.
 *
 * @param {Array} blocks - Array of Lookbook blocks.
 * @param {Array} productCardLocation - Lprop data for product card atc
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import ProductCard from '~global/product-card/product-card'
import KlaviyoBisMessage from '~async/klaviyo-bis/klaviyo-bis-message.vue'
import ResponsiveImage from '~global/images/responsive-image'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import IconCart from '~icons/general/cart.svg'
import IconClose from '~icons/general/close.svg'
import LookbookProductGridModal from './lookbook-product-grid-modal.vue'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

export default {
  name: 'LookbookImages',

  components: {
    HorizontalScrollManager: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.horizontal-scroll-manager' */
        '~async/horizontal-scroll-manager/horizontal-scroll-manager'
      ),
    }),
    IconArrowLeft,
    IconArrowRight,
    IconCart,
    IconClose,
    ProductCard,
    QuickAddToCart: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.quick-add-to-cart' */
        '~async/quick-add-to-cart/quick-add-to-cart'
      ),
    }),
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
    KlaviyoBisMessage,
    LookbookProductGridModal,
  },
  inheritAttrs: false,
  props: {
    enableHorizontalScroll: {
      type: Boolean,
      default: false,
    },
    doubleItemMobile: {
      type: Boolean,
      default: false,
    },
    imageGalleryGrid: {
      type: Boolean,
      default: false,
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    lazyload: {
      type: Boolean,
      default: false,
    },
    productCardLocation: {
      type: Object,
      default: () => ({
        key: 'sectionHotspot',
      }),
    },
  },

  data() {
    return {
      visibleProductsBlockIndex: -1,
      blockConfigs: [],
      showBackInStock: false,
      productGridModalActive: false,
      blockIndex: false,
      showSuccessMessage: false,
      cardBackground: '--color-brand-light-gray',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
      shopTheLookSelectedProducts: (state) => state.shopTheLook.selectedProducts,
      shopTheLookBlockConfigs: (state) => state.shopTheLook.blockConfigs,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      return this.$string('product.cta.add_to_cart')
    },
  },

  watch: {
    lastAddedProductId() {
      if (this.lastAddedProductId) {
        this.closeAtcWithId()
      }
    },
  },

  created() {
    window.cnvs?.EventBus?.listen('overlay:close-product-grid', () => {
      this.closeProductGridModal()
    })
  },

  beforeMount() {
    this.initBlockObjects()

    if (this.blocks?.filter((block) => block.video).length) {
      this.loadStylesheet('video-player')
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      loadStylesheet: 'components/loadStylesheet',
      setStlProducts: 'shopTheLook/setProducts',
      setStlBlockConfigs: 'shopTheLook/setBlockConfigs',
      setStlIndex: 'shopTheLook/setIndex',
      setLocation: 'shopTheLook/setLocation',
    }),

    closeProductGridModal() {
      this.productGridModalActive = false
      enableBodyScroll(this.$refs.scrollElement)
    },

    initBlockObjects() {
      this.blocks.forEach(() => {
        this.blockConfigs.push({
          selectedProducts: [],
          visibleProductsBlockIndex: -1,
          productSelected: false,
          viewedProduct: {},
          viewedProductIndex: 0,
          currentSplide: {},
          cardBackground: '--color-brand-light-gray',
        })
      })
    },

    getImageBackgroundColor(block) {
      return block.cardBackground
    },

    getBlockTitleClasses(block) {
      const classes = {}
      const fontSize = block.titleFontSize ? block.titleFontSize : 'text-body-2xs-regular'
      const fontSizeMobile = block.titleFontSizeMobile ? block.titleFontSizeMobile : 'text-setting-heading-mobile-xs'
      const fontWeightMobile = block.titleFontWeightMobile ? block.titleFontWeightMobile : 'font-weight-medium-mobile'
      const fontWeightDesktop = block.titleFontWeightDesktop ? block.titleFontWeightDesktop : 'font-weight-medium-desktop'
      const fontFamily = block.titleFontFamily ? block.titleFontFamily : 'font-family-apercu'

      classes[fontSize] = true
      classes[fontSizeMobile] = true
      classes[fontWeightMobile] = true
      classes[fontWeightDesktop] = true
      classes[fontFamily] = true

      return classes
    },

    getBlockClasses(singleItem) {
      const classes = {}

      if (this.imageGalleryGrid) {
        classes.l16 = true
        classes.xs12 = this.doubleItemMobile
        classes.xs24 = !this.doubleItemMobile
      } else {
        classes['l16-48'] = singleItem
        classes.l32 = !singleItem
        classes.xs24 = true
      }

      return classes
    },

    showProducts(index, block) {
      if (this.imageGalleryGrid && !this.isMobile && block.products.length) {
        this.blockConfigs[index].cardBackground = block.cardBackground
        this.blockConfigs[index].selectedProducts = block.products
        this.shopTheLookSelectedProducts = block.products
        this.shopTheLookBlockConfigs = this.blockConfigs
        this.blockIndex = index
        this.productGridModalActive = true
        this.setStlProducts(block.products)
        this.setStlBlockConfigs(this.blockConfigs)
        this.setStlIndex(index)
        this.setLocation(this.productCardLocation)
        disableBodyScroll(this.$refs.scrollElement)
      } else if (this.isMobile) {
        this.showOverlay(index, block, 'lookbook-product-list-modal')
      } else {
        this.toggleProducts(index, block)
      }
    },

    /**
    * Open product list overlay.
    */
    showOverlay(index, block, component) {
      this.blockConfigs[index].cardBackground = block.cardBackground
      this.blockConfigs[index].selectedProducts = block.products
      this.shopTheLookSelectedProducts = block.products
      this.shopTheLookBlockConfigs = this.blockConfigs
      this.setStlProducts(block.products)
      this.setStlBlockConfigs(this.blockConfigs)
      this.setStlIndex(index)
      this.setLocation(this.productCardLocation)
      this.openOverlay({
        ignoreDismissed: true,
        namespace: component,
      })
    },

    /**
     * Show/switch/hide product list
     */
    toggleProducts(index, block) {
      if (index >= 0) {
        this.blockConfigs[index].selectedProducts = block.products
        this.blockConfigs[index].cardBackground = block.cardBackground
      }

      this.visibleProductsBlockIndex = index
      this.blockConfigs.forEach((_, configIndex) => {
        if (configIndex !== index) {
          this.closeAtc(configIndex)
        }
      })
    },

    /**
      * Get video MP4 source object.
      * @param {Array} sources - Video sources.
      * @returns {Object}
      */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },

    /**
     * Get splide configs per lookbook state
     * @param {Number} productCount - Products of lookbook.
     */
    getSplideConfigs(productCount, index) {
      const showPerView = productCount === 4 ? 4 : 3

      return {
        autoplay: false,
        type: 'slide',
        perPage: this.blockConfigs[index]?.productSelected ? 1 : showPerView,
        direction: 'ltr',
        drag: false,
        speed: this.$timing('normal'),
        start: 0,
        pauseOnHover: false,
        arrows: false,
        pagination: false,
      }
    },

    /**
     * Checks variant ids of the products from the each block and resets
     * the block where id was found
     */
    closeAtcWithId() {
      this.blocks.forEach((block, index) => {
        if (block.products.some((product) => {
          const variantIds = product.variants.map((variant) => variant.id)

          return variantIds.includes(Number(this.lastAddedProductId))
        })) {
          this.closeAtc(index)
        }
      })
    },

    /**
     * Close ATC and show products only
     */
    closeAtc(blockIndex) {
      if (this.blockConfigs[blockIndex]?.currentSplide?.go) {
        this.blockConfigs[blockIndex].currentSplide.go(0)
      }

      if (this.blockConfigs[blockIndex]) {
        const customFocus = document.querySelector(this.blockConfigs[blockIndex].returnFocus)
        if (this.blockConfigs[blockIndex].viewedProduct && customFocus) {
          customFocus.focus()
        }
        this.blockConfigs[blockIndex].productSelected = false
        this.blockConfigs[blockIndex].viewedProduct = {}
      }

      if (this.showBackInStock) {
        this.showBackInStock = false
      }
    },

    /**
     * Get image
     */
    getImage(image) {
      if (image.src) {
        return image
      }

      return {
        src: image,
      }
    },

    /**
     * Handle add to cart.
     */
    handleAddToCart(blockIndex, productsIndex) {
      this.blockConfigs[blockIndex].viewedProduct =
        this.blockConfigs[blockIndex].selectedProducts[productsIndex]
      this.blockConfigs[blockIndex].productSelected = true
      this.blockConfigs[blockIndex].currentSplide = this.$refs[`splide-${blockIndex}`][0]
      this.blockConfigs[blockIndex].viewedProductIndex = productsIndex
      this.blockConfigs[blockIndex].returnFocus = `#splide-${blockIndex}-${productsIndex}`
      this.blockConfigs[blockIndex].currentSplide.splide.event.on('resized', () => {
        this.blockConfigs[blockIndex].currentSplide
          .go(this.blockConfigs[blockIndex].viewedProductIndex)
      })

      this.blockConfigs[blockIndex].currentSplide.splide.event.on('moved', (newIndex) => {
        this.blockConfigs[blockIndex].viewedProductIndex = newIndex
        this.blockConfigs[blockIndex].viewedProduct =
          this.blockConfigs[blockIndex].selectedProducts[newIndex]
      })
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * Handle back in stock success message.
     */
    handleBISSuccess() {
      cnvs.EventBus.emit('dy:bis')
    },
  },
}
</script>

<style lang="scss">
@import './lookbook-images';
</style>
